import { Link, useSearchParams } from 'react-router-dom'
import { t } from '../i18n/lingua'

/**
 * Pezzi condivisi dalle pagine del traffico.
 *
 * Le tre pagine — conversazioni, entry point, esportatori — sono viste diverse
 * sugli stessi dati, e due cose devono restare identiche fra loro: la finestra
 * temporale e il modo di mostrare un estremo della conversazione. Duplicarle
 * significherebbe che al primo ritocco una pagina dice «2 ore fa» e l'altra
 * «poco fa» per lo stesso istante, e la differenza si legge come un difetto.
 */

// Le etichette sono funzioni: `t()` va chiamata al momento di mostrarle, non
// all'import del modulo.
export const FLOW_WINDOWS: { key: string; label: () => string }[] = [
  { key: '1h', label: () => t('Ultima ora') },
  { key: '6h', label: () => t('6 ore') },
  { key: '24h', label: () => t('24 ore') },
  { key: '7d', label: () => t('7 giorni') },
  { key: '30d', label: () => t('30 giorni') },
]

const DEFAULT_WINDOW = '24h'

/**
 * La finestra vive nell'URL, non in uno stato locale.
 *
 * Sono tre pagine di una stessa sezione e ci si passa in continuazione: con uno
 * stato per pagina, ogni cambio di scheda riporterebbe a 24 ore quello che si
 * stava guardando su 7 giorni — e chi lo subisce non lo legge come una
 * scomodità, lo legge come un dato che è cambiato.
 *
 * In più un URL con la finestra dentro si può incollare in un ticket, che per un
 * dato osservato in una finestra è metà dell'informazione.
 */
export function useFlowWindow(): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('window') ?? DEFAULT_WINDOW
  // Un valore inventato nell'URL non deve propagarsi all'API, che risponderebbe
  // 400 su una pagina che si è solo aperta con un link storto.
  const value = FLOW_WINDOWS.some((w) => w.key === raw) ? raw : DEFAULT_WINDOW

  return [
    value,
    (next: string) => {
      const updated = new URLSearchParams(params)
      updated.set('window', next)
      // `replace`: il tasto indietro deve riportare alla pagina precedente, non
      // ripercorrere a ritroso le finestre provate.
      setParams(updated, { replace: true })
    },
  ]
}

export function WindowPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="toolbar">
      {FLOW_WINDOWS.map((w) => (
        <button
          key={w.key}
          className={value === w.key ? '' : 'secondary'}
          onClick={() => onChange(w.key)}
        >
          {w.label()}
        </button>
      ))}
    </div>
  )
}

/**
 * Un estremo della conversazione.
 *
 * Se è un CI inventariato porta al suo dettaglio; altrimenti resta l'indirizzo
 * nudo — che è proprio l'informazione interessante, perché segnala qualcosa che
 * parla nella rete e non è in CMDB.
 */
export function Endpoint({ id, label, addr, ptr, desc }: {
  id: string | null
  label: string
  addr: string
  /** Reverse DNS name and what a person wrote it is (address inventory). */
  ptr?: string | null
  desc?: string | null
}) {
  // Il ponte verso la mappa di correlazione, da ogni punto in cui un indirizzo
  // compare: la mappa si apre «partendo da qui» invece di chiedere di
  // ritrascrivere l'indirizzo — che è il momento in cui si sbaglia una cifra.
  const mappa = (
    <Link
      to={`/flows/map?seed=${encodeURIComponent(addr)}`}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 11 }}
      title={t('Apri la mappa di correlazione partendo da questo indirizzo')}
    >
      {t('Mappa')}
    </Link>
  )
  if (!id) {
    // Not a CI: the address inventory says what it is — the name a person
    // gave first, then the reverse DNS name.
    return (
      <>
        <span className="mono">{addr}</span>
        {desc && <div style={{ fontSize: 12 }} title={desc}>{desc.length > 60 ? `${desc.slice(0, 60)}…` : desc}</div>}
        {ptr && <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }} title={t('Nome DNS inverso (PTR)')}>{ptr}</div>}
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          <Link to={`/addresses?q=${encodeURIComponent(addr)}`} onClick={(e) => e.stopPropagation()}
            title={t('Apri nell’inventario indirizzi: scrivi cos’è')}>{t('Non inventariato')}</Link> · {mappa}
        </div>
      </>
    )
  }
  return (
    <>
      <Link to={`/cis/${id}`} onClick={(e) => e.stopPropagation()}>
        {label}
      </Link>
      <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {addr} · {mappa}
      </div>
    </>
  )
}
